/**
 * Editor Toolbar — Grouped formatting buttons for the Tiptap editor.
 * Ported from PaaS EditorToolbar.tsx (260 lines)
 *
 * Groups: Headings | Text Sizes | Text Format | Lists | Alignment | Quotes & Links & Code
 */
import { useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import {
  Bold, Italic, Underline, Code, List, ListOrdered, CheckSquare,
  AlignLeft, AlignCenter, AlignRight, Quote, Link as LinkIcon, Code2,
  Image as ImageIcon, ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { sanitizeUrl } from '@/lib/sanitize';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';

/**
 * Typed helper for Tiptap Image extension's setImage command.
 * The Image extension augments the chain but TypeScript can't
 * infer it from the generic Editor type — this is the standard workaround.
 */
function setEditorImage(editor: Editor, src: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (editor.chain().focus() as any).setImage({ src }).run();
}

interface EditorToolbarProps {
  editor: Editor | null;
}

export function EditorToolbar({ editor }: EditorToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!editor) return null;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // W2-G-10 + #703 fix: upload to /wiki/upload-image — backend
    // persists the bytes under UPLOADS_DIR/{org}/wiki-images/{uuid}.{ext}
    // and returns { url, filename, size, mimeType }.
    //
    // Pre-fix the catch fell back to FileReader.readAsDataURL and
    // embedded a base64 data URL into the article body. That defeats
    // the entire point of the hardened upload path: MIME validation,
    // SVG sanitization, magic-byte checks, tenant-scoped storage, and
    // image size limits all became bypassable from the client. The
    // route was rejecting a malformed file and the editor was happily
    // inlining it anyway.
    //
    // New behaviour: if the server says no, the image stays out of the
    // article and the user sees a toast with the server's reason
    // (oversize / unsupported MIME / hardened scanner rejection). No
    // automatic fallback.
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { uploadFile } = await import('@/lib/upload-utils');
      const { url } = await uploadFile<{ url: string }>('/wiki/upload-image', formData);
      setEditorImage(editor, url);
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(
          err,
          `Image upload was rejected. Please try a different file (PNG/JPEG/GIF/WebP, under 10 MB).`,
        ),
      );
    }
    e.target.value = '';
  };

  const handleAddLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const rawUrl = window.prompt('URL', previousUrl || 'https://');
    if (rawUrl === null) return;
    if (rawUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const safeUrl = sanitizeUrl(rawUrl);
    if (!safeUrl) { return; } // Block javascript: and other dangerous URLs
    editor.chain().focus().extendMarkRange('link').setLink({ href: safeUrl }).run();
  };

  const handleInsertImageUrl = () => {
    const rawUrl = window.prompt('Enter image URL');
    if (!rawUrl) return;
    const safeUrl = sanitizeUrl(rawUrl);
    if (!safeUrl) { return; }
    setEditorImage(editor, safeUrl);
  };

  // Button helper.
  // L5 follow-up — the round-2 retest could not discover the underline
  // affordance via browser automation. Round 1 fixed the markdown
  // serializer to emit `<u>` instead of `__` (markdown bold), but the
  // toolbar button only carried `title=`, which Codex/Playwright can
  // miss because role-by-name selectors prefer aria-label. Surface
  // both attributes so accessibility, screen-reader, and automation
  // selectors all find the button by its function (Bold/Italic/
  // Underline/Inline Code/etc.).
  const TB = ({ onClick, active, children, label }: { onClick: () => void; active: boolean; children: React.ReactNode; label?: string }) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7 p-0', active ? 'bg-muted text-foreground' : 'text-muted-foreground')}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {children}
    </Button>
  );

  // Text button helper (for H1, H2, N1, N2)
  const TBText = ({ onClick, active, children, label }: { onClick: () => void; active: boolean; children: React.ReactNode; label?: string }) => (
    <Button
      type="button"
      variant="ghost"
      className={cn('h-7 px-2 text-[10px] font-bold', active ? 'bg-muted text-foreground' : 'text-muted-foreground')}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {children}
    </Button>
  );

  const Divider = () => <div className="w-px h-4 bg-border mx-1" />;

  return (
    <div className="flex items-center gap-1 px-4 py-2 border-b bg-card sticky top-0 z-10 flex-wrap">
      {/* Group 1: Headings */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TBText onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} label="Heading 1">H1</TBText>
        <TBText onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} label="Heading 2">H2</TBText>
      </div>

      {/* Group 2: Normal Text */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TBText onClick={() => editor.chain().focus().setParagraph().run()} active={!editor.isActive('heading') && !editor.isActive('codeBlock')} label="Paragraph">P</TBText>
      </div>

      <Divider />

      {/* Group 3: Text Formatting */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold"><Bold className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic"><Italic className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} label="Underline"><Underline className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} label="Inline Code"><Code className="h-3.5 w-3.5" /></TB>
      </div>

      <Divider />

      {/* Group 4: Lists */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet List"><List className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Numbered List"><ListOrdered className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} label="Task List"><CheckSquare className="h-3.5 w-3.5" /></TB>
      </div>

      <Divider />

      {/* Group 5: Alignment */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TB onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} label="Align Left"><AlignLeft className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} label="Align Center"><AlignCenter className="h-3.5 w-3.5" /></TB>
        <TB onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} label="Align Right"><AlignRight className="h-3.5 w-3.5" /></TB>
      </div>

      <Divider />

      {/* Group 6: Blockquote, Link, Image, Code Block */}
      <div className="flex items-center bg-background border rounded-md p-0.5">
        <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} label="Blockquote"><Quote className="h-3.5 w-3.5" /></TB>
        <TB onClick={handleAddLink} active={editor.isActive('link')} label="Link"><LinkIcon className="h-3.5 w-3.5" /></TB>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className={cn('h-7 w-7 p-0 text-muted-foreground')}
              title="Insert image"
              aria-label="Insert image"
            >
              <ImageIcon className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()} className="text-xs">Upload from Computer</DropdownMenuItem>
            <DropdownMenuItem onClick={handleInsertImageUrl} className="text-xs">Insert from URL</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <TB onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} label="Code Block"><Code2 className="h-3.5 w-3.5" /></TB>
      </div>

      {/* Hidden file input for image upload */}
      <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
    </div>
  );
}
